import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '../../../shared/ui/hooks-ui/useDebouncedValue';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { subscribeCatalogSynced } from '../../../sync/core/catalogSyncListeners';
import { effectiveProductSearchQuery, PRODUCT_SEARCH_DEBOUNCE_MS } from '../../../shared/product-search/productSearchUx';
import { useCatalogOverlayStore } from '../../store/useCatalogOverlayStore';
import { createCatalogCsvAdapter } from '../../infra/catalogCsvAdapter';
import { useProductVariantsEnabled } from '../../application/useProductVariantsEnabled';
import { listActiveBrandsLocal } from '../../infra/brandsRepo';
import { listActiveSuppliersLocal } from '../../infra/suppliersRepo';
import { syncCatalogSnapshotFromRemote } from '../../infra/catalogSnapshotSync';
import { useCatalogListController } from '../../application/useCatalogListController';
import {
  buildCategoryFilterOptions,
  buildCategoryOptions,
  buildCategoryOptionsWithFallback,
} from '../../domain/categoryOptions';
import {
  categoryAppliesToDraftItemType,
  getCategoryDisplayLabel,
  normalizeProductCategoryId,
} from '../../domain/categoryTypes';
import { createCategoryDisplayResolver } from '../../domain/catalogEventSummary';
import {
  DEFAULT_CATALOG_FILTERS,
  hasActiveCatalogFilters,
} from '../../domain/catalogFilterOptions';
import type { CategoryRecord } from '../../domain/categoryTypes';
import type { CatalogItemTypeFilter, Product } from '../../domain/productTypes';
import type { CatalogFilter, CatalogSort, ProductSearchRow } from '../../domain/catalogTypes';
import type { ProductDraft } from '../../domain/productDraft';
import { emptyDraft } from '../../domain/productDraft';
import { useCatalogCategoryManager } from '../hooks/useCatalogCategoryManager';

// ─── Master options (brands + suppliers) ────────────────────────────────────

function useMasterOptions(refreshCategories: () => void | Promise<void>) {
  const [catalogBrandOptions, setCatalogBrandOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [catalogSupplierOptions, setCatalogSupplierOptions] = useState<Array<{ id: string; name: string }>>([]);

  const refreshCatalogMasterOptions = useCallback(async (options?: { forceRemote?: boolean }) => {
    const forceRemote = options?.forceRemote === true;
    // Mount/focus: solo local. Full remoto solo pull manual (forceRemote).
    if (forceRemote) {
      try {
        await syncCatalogSnapshotFromRemote({
          hasSession: () => !!useAuthStore.getState().session,
          force: true,
          forceFull: true,
        });
      } catch (e) {
        console.warn('[refreshCatalogMasterOptions] sync failed, using local data:', e);
      }
    }
    const [brandsMaster, suppliers] = await Promise.all([
      listActiveBrandsLocal(),
      listActiveSuppliersLocal(),
    ]);
    setCatalogBrandOptions(brandsMaster);
    setCatalogSupplierOptions(suppliers);
  }, []);

  useEffect(() => {
    void refreshCatalogMasterOptions();
  }, [refreshCatalogMasterOptions]);

  useEffect(() => {
    const unsubscribeCatalogSynced = subscribeCatalogSynced(() => {
      void refreshCatalogMasterOptions();
    });
    return unsubscribeCatalogSynced;
  }, [refreshCatalogMasterOptions]);

  return { catalogBrandOptions, catalogSupplierOptions, refreshCatalogMasterOptions };
}

// ─── Category screen state (derived from filters + categories) ──────────────

function useCategoryScreenState(options: {
  categories: CategoryRecord[];
  itemTypeFilter: CatalogItemTypeFilter;
  categoryFilter: Product['categoryId'] | 'all';
  draft: ProductDraft;
  serviceCatalogEnabled: boolean;
  setCategoryFilter: (value: Product['categoryId'] | 'all') => void;
  setItemTypeFilter: (value: CatalogItemTypeFilter) => void;
  setDraft: (value: ProductDraft | ((prev: ProductDraft) => ProductDraft)) => void;
}) {
  const {
    categories, itemTypeFilter, categoryFilter, draft,
    serviceCatalogEnabled,
    setCategoryFilter,
    setItemTypeFilter,
    setDraft,
  } = options;

  const categoryFallbackSource = useMemo<CategoryRecord[]>(() => [], []);

  const catalogActiveCategoryOptions = useMemo(
    () => buildCategoryOptions(categories, { fallbackCategories: categoryFallbackSource }),
    [categories, categoryFallbackSource],
  );

  const catalogDefaultPhysicalCategory = useMemo(() => {
    const opts = buildCategoryOptions(categories, {
      fallbackCategories: categoryFallbackSource, forItemType: 'physical',
    });
    return opts[0]?.id ?? emptyDraft.categoryId;
  }, [categories, categoryFallbackSource]);

  /** Siempre producto físico; servicio solo vía toggle o deep-link (`pendingOpenNewService`). */
  const defaultItemTypeForNew: ProductDraft['itemType'] = 'physical';

  const catalogDefaultCategoryForNewItem = catalogDefaultPhysicalCategory;

  const categoryFilters = useMemo(() => {
    const forItemType =
      itemTypeFilter === 'service'
        ? 'service'
        : itemTypeFilter === 'physical' ||
            itemTypeFilter === 'combo' ||
            itemTypeFilter === 'recipe'
          ? 'physical'
          : undefined;
    return buildCategoryFilterOptions(categories, {
      allLabel: 'Todas', fallbackCategories: categoryFallbackSource, forItemType,
    });
  }, [categories, categoryFallbackSource, itemTypeFilter]);

  const selectedCategoryRecord = useMemo(() => {
    if (categoryFilter === 'all') return null;
    const normalizedCategoryId = normalizeProductCategoryId(categoryFilter);
    return categories.find((c) => normalizeProductCategoryId(c.slug) === normalizedCategoryId) ?? null;
  }, [categories, categoryFilter]);

  const handleCatalogItemTypeFilterChange = useCallback(
    (nextFilter: CatalogItemTypeFilter) => {
      setItemTypeFilter(nextFilter);
      const categoryItemType =
        nextFilter === 'service' ? 'service' : 'physical';
      const categoryStillMatches =
        nextFilter === 'all' ||
        selectedCategoryRecord == null ||
        categoryAppliesToDraftItemType(selectedCategoryRecord.appliesTo, categoryItemType);
      if (!categoryStillMatches) setCategoryFilter('all');
    },
    [selectedCategoryRecord, setCategoryFilter, setItemTypeFilter],
  );

  useEffect(() => {
    if (categoryFilter === 'all') return;
    const normalizedCategoryId = normalizeProductCategoryId(categoryFilter);
    const isAvailable = categoryFilters.some(
      (f) => normalizeProductCategoryId(f.id) === normalizedCategoryId,
    );
    if (!isAvailable) setCategoryFilter('all');
  }, [categoryFilter, categoryFilters, setCategoryFilter]);

  const formCategoryOptions = useMemo(() => {
    return buildCategoryOptionsWithFallback(categories, draft.categoryId, {
      fallbackCategories: categoryFallbackSource, forItemType: draft.itemType,
    });
  }, [categories, categoryFallbackSource, draft.categoryId, draft.itemType]);

  const handleProductItemTypeChange = useCallback(
    (itemType: ProductDraft['itemType']) => {
      setDraft((current) => {
        const next = { ...current, itemType };
        // Services only support unit|hour; keep draft in sync with the segmented control.
        if (
          itemType === 'service' &&
          current.unitOfMeasure !== 'unit' &&
          current.unitOfMeasure !== 'hour'
        ) {
          next.unitOfMeasure = 'unit';
          next.sellByWeight = false;
        }
        const idNorm = normalizeProductCategoryId(current.categoryId);
        const catRecord = categories.find(
          (c) => normalizeProductCategoryId(c.id) === idNorm || normalizeProductCategoryId(c.slug) === idNorm,
        );
        if (current.categoryId?.trim() && catRecord && !categoryAppliesToDraftItemType(catRecord.appliesTo, itemType)) {
          return { ...next, categoryId: null };
        }
        return next;
      });
    },
    [categories, setDraft],
  );

  const categoryDisplayResolver = useMemo(() => createCategoryDisplayResolver(categories), [categories]);

  const getCategoryLabel = useCallback(
    (slug?: string | null) => {
      if (!slug) return 'Sin categoría';
      const label = categoryDisplayResolver(slug);
      if (label !== '—' && label !== String(slug).trim()) return label;
      // Sin registro en el resolver (snapshot aún no cargado o categoría de otra
      // vista): humanizar el slug — nunca mostrar `cat-almacen` crudo en la fila.
      return getCategoryDisplayLabel(slug) ?? 'Sin categoría';
    },
    [categoryDisplayResolver],
  );
  const getCategoryIsRestricted = useCallback(
    (categoryId?: string | null) => {
      if (!categoryId) return false;
      const normalizedId = normalizeProductCategoryId(categoryId);
      return categories.some(
        (category) =>
          category.isRestricted &&
          (normalizeProductCategoryId(category.id) === normalizedId ||
            normalizeProductCategoryId(category.slug) === normalizedId),
      );
    },
    [categories],
  );

  return {
    catalogActiveCategoryOptions,
    catalogDefaultCategoryForNewItem,
    defaultItemTypeForNew,
    categoryFilters,
    formCategoryOptions,
    getCategoryLabel,
    getCategoryIsRestricted,
    handleCatalogItemTypeFilterChange,
    handleProductItemTypeChange,
  };
}

// ─── Main view model ────────────────────────────────────────────────────────

type Params = {
  serviceCatalogEnabled: boolean;
  draft: ProductDraft;
  setDraft: (value: ProductDraft | ((prev: ProductDraft) => ProductDraft)) => void;
  canEdit: boolean;
  maxProducts: number | null;
  performedByUserId: string | null | undefined;
};

export function useCatalogListVm({
  serviceCatalogEnabled,
  draft,
  setDraft,
  canEdit,
  maxProducts,
  performedByUserId,
}: Params) {
  const setFiltersExpanded = useCatalogOverlayStore((state) => state.setFiltersExpanded);
  const setLookupFeedback = useCatalogOverlayStore((state) => state.setLookupFeedback);
  const setRemoteSuggestion = useCatalogOverlayStore((state) => state.setRemoteSuggestion);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);

  const categoryManager = useCatalogCategoryManager();
  const { categories, refreshCategories } = categoryManager;

  const { catalogBrandOptions, catalogSupplierOptions, refreshCatalogMasterOptions } =
    useMasterOptions(refreshCategories);

  // ── Search + filters state ──
  const [searchQuery, setSearchQuery] = useState('');
  const catalogSearchDebounced = useDebouncedValue(searchQuery.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);
  const catalogListSearchQuery = useMemo(
    () => effectiveProductSearchQuery(catalogSearchDebounced),
    [catalogSearchDebounced],
  );
  const [statusFilter, setStatusFilter] = useState<CatalogFilter>(DEFAULT_CATALOG_FILTERS.statusFilter);
  const [categoryFilter, setCategoryFilter] = useState<Product['categoryId'] | 'all'>(DEFAULT_CATALOG_FILTERS.categoryFilter);
  const [itemTypeFilter, setItemTypeFilter] = useState<CatalogItemTypeFilter>(DEFAULT_CATALOG_FILTERS.itemTypeFilter);
  const [catalogBrandFilter, setCatalogBrandFilter] = useState<string | null>(DEFAULT_CATALOG_FILTERS.brandFilter);
  const [catalogSupplierFilter, setCatalogSupplierFilter] = useState<string | null>(DEFAULT_CATALOG_FILTERS.supplierFilter);
  const [sortBy, setSortBy] = useState<CatalogSort>(DEFAULT_CATALOG_FILTERS.sortBy);
  const [alertFilter, setAlertFilter] = useState(DEFAULT_CATALOG_FILTERS.alertFilter);
  const [incompleteFilter, setIncompleteFilter] = useState(DEFAULT_CATALOG_FILTERS.incompleteFilter);
  const [pinnedCatalogProduct, setPinnedCatalogProduct] = useState<{ id: string; label: string } | null>(null);
  const clearPinnedCatalogSearch = useCallback(() => setPinnedCatalogProduct(null), []);

  const onCatalogSearchFieldChange = useCallback(
    (text: string) => {
      if (pinnedCatalogProduct != null) clearPinnedCatalogSearch();
      setLookupFeedback(null);
      setSearchQuery(text);
    },
    [pinnedCatalogProduct, clearPinnedCatalogSearch, setLookupFeedback],
  );

  // ── List controller ──
  const {
    catalogListRef,
    catalogWideScrollRef,
    products,
    catalogPageIndex,
    catalogTotalCount,
    pageSize,
    setCatalogPageSize,
    hasMore,
    catalogLoading,
    catalogError,
    catalogCriteriaRefreshing,
    isRefreshing,
    loadFirstPage,
    handleRefresh,
    handleRefreshAfterLocalEdit,
    loadCatalogNextPage,
    loadCatalogPreviousPage,
    goToCatalogPage,
  } = useCatalogListController({
    searchQuery: catalogListSearchQuery,
    pinnedProductId: pinnedCatalogProduct?.id ?? null,
    statusFilter,
    categoryFilter,
    itemTypeFilter,
    brandFilter: catalogBrandFilter,
    supplierFilter: catalogSupplierFilter,
    sortBy,
    alertFilter,
    incompleteFilter,
    branchId: currentBranchId ?? null,
    refreshCategories,
  });

  const handleCatalogRefresh = useCallback(async () => {
    await refreshCatalogMasterOptions({ forceRemote: true });
    await handleRefresh();
  }, [handleRefresh, refreshCatalogMasterOptions]);

  const loadFn = useCallback(async (opts?: { force?: boolean }) => {
    if (opts?.force) {
      await refreshCatalogMasterOptions({ forceRemote: true });
      await handleRefresh();
    } else {
      void refreshCategories();
      void refreshCatalogMasterOptions();
    }
  }, [refreshCatalogMasterOptions, handleRefresh, refreshCategories]);

  // ── Category screen state ──
  const {
    catalogActiveCategoryOptions,
    catalogDefaultCategoryForNewItem,
    defaultItemTypeForNew,
    categoryFilters,
    formCategoryOptions,
    getCategoryLabel,
    getCategoryIsRestricted,
    handleCatalogItemTypeFilterChange,
    handleProductItemTypeChange,
  } = useCategoryScreenState({
    categories,
    itemTypeFilter,
    categoryFilter,
    draft,
    serviceCatalogEnabled,
    setCategoryFilter,
    setItemTypeFilter,
    setDraft,
  });

  // alertFilter/incompleteFilter ahora se aplican server-side (SQL WHERE + web fallback).
  // No se filtran client-side para que la paginación y el totalCount sean correctos.

  // `sortBy` no cuenta como filtro activo para la UI del listado: cambiar el
  // orden no reduce resultados, así que no debe mutar el KPI a «Resultados»
  // ni disparar el empty «Nada coincide con los filtros».
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    pinnedCatalogProduct != null ||
    hasActiveCatalogFilters({
      itemTypeFilter,
      statusFilter,
      categoryFilter,
      sortBy: DEFAULT_CATALOG_FILTERS.sortBy,
      brandFilter: catalogBrandFilter,
      supplierFilter: catalogSupplierFilter,
      alertFilter,
      incompleteFilter,
    });

  const clearFilters = useCallback(() => {
    setLookupFeedback(null);
    setRemoteSuggestion(null);
    setSearchQuery('');
    setPinnedCatalogProduct(null);
    setStatusFilter(DEFAULT_CATALOG_FILTERS.statusFilter);
    setCategoryFilter(DEFAULT_CATALOG_FILTERS.categoryFilter);
    setItemTypeFilter(DEFAULT_CATALOG_FILTERS.itemTypeFilter);
    setCatalogBrandFilter(DEFAULT_CATALOG_FILTERS.brandFilter);
    setCatalogSupplierFilter(DEFAULT_CATALOG_FILTERS.supplierFilter);
    setSortBy(DEFAULT_CATALOG_FILTERS.sortBy);
    setAlertFilter(DEFAULT_CATALOG_FILTERS.alertFilter);
    setIncompleteFilter(DEFAULT_CATALOG_FILTERS.incompleteFilter);
    setFiltersExpanded(false);
  }, [setFiltersExpanded, setLookupFeedback, setRemoteSuggestion]);

  const handlePickCatalogSearchSuggestion = useCallback((row: ProductSearchRow) => {
    setLookupFeedback(null);
    setPinnedCatalogProduct({ id: row.id, label: row.name });
    setSearchQuery('');
  }, [setLookupFeedback]);

  // ── Brand label lookup (O(1) via Map) ──
  const brandLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of catalogBrandOptions) m.set(b.id, b.name);
    return m;
  }, [catalogBrandOptions]);
  const getBrandLabel = useCallback(
    (id?: string | null): string | null => {
      if (!id) return null;
      return brandLabelMap.get(id) ?? null;
    },
    [brandLabelMap],
  );

  // ── Masters pane (modal overlay, no shell navigation) ──
  const [mastersModalVisible, setMastersModalVisible] = useState(false);
  const [mastersInitialSection, setMastersInitialSection] = useState<
    'categories' | 'brands' | 'suppliers'
  >('categories');
  const openMastersHub = useCallback((section?: 'categories' | 'brands' | 'suppliers') => {
    setMastersInitialSection(section ?? 'categories');
    setMastersModalVisible(true);
  }, []);
  const closeMastersHub = useCallback(() => setMastersModalVisible(false), []);

  // ── CSV import pane ──
  const [csvModalVisible, setCsvModalVisible] = useState(false);
  const openCsvModal = useCallback(() => setCsvModalVisible(true), []);
  const closeCsvModal = useCallback(() => setCsvModalVisible(false), []);
  const variantsEnabled = useProductVariantsEnabled();
  const csvImportModalProps = useMemo(
    () => ({
      visible: csvModalVisible,
      onClose: closeCsvModal,
      mode: 'catalog' as const,
      catalogEntity: 'products' as const,
      kicker: 'Catálogo',
      canEdit,
      maxProducts,
      performedByUserId: performedByUserId ?? null,
      onApplied: handleRefreshAfterLocalEdit,
      adapter: createCatalogCsvAdapter('products', maxProducts, performedByUserId ?? null, variantsEnabled),
    }),
    [csvModalVisible, closeCsvModal, canEdit, maxProducts, performedByUserId, handleRefreshAfterLocalEdit, variantsEnabled],
  );

  return {
    // category manager
    categories,
    activeCategoryOptions: categoryManager.activeCategoryOptions,
    // master options
    catalogBrandOptions,
    catalogSupplierOptions,
    refreshCatalogMasterOptions,
    // search + filters
    searchQuery,
    setSearchQuery,
    onCatalogSearchFieldChange,
    pinnedCatalogProduct,
    clearPinnedCatalogSearch,
    handlePickCatalogSearchSuggestion,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    itemTypeFilter,
    setItemTypeFilter: handleCatalogItemTypeFilterChange,
    sortBy,
    setSortBy,
    catalogBrandFilter,
    setCatalogBrandFilter,
    catalogSupplierFilter,
    setCatalogSupplierFilter,
    alertFilter,
    setAlertFilter,
    incompleteFilter,
    setIncompleteFilter,
    // list
    catalogListRef,
    catalogWideScrollRef,
    products,
    catalogPageIndex,
    catalogTotalCount,
    pageSize,
    setCatalogPageSize,
    hasMore,
    catalogLoading,
    catalogError,
    loadFirstPage,
    loadFn,
    handleRefreshAfterLocalEdit,
    loadCatalogNextPage,
    loadCatalogPreviousPage,
    goToCatalogPage,
    hasActiveFilters,
    clearFilters,
    // category screen state
    catalogDefaultCategoryForNewItem,
    defaultItemTypeForNew,
    categoryFilters,
    formCategoryOptions,
    getCategoryLabel,
    getCategoryIsRestricted,
    getBrandLabel,
    handleProductItemTypeChange,
    // masters + import
    openMastersHub,
    closeMastersHub,
    mastersModalVisible,
    mastersInitialSection,
    csvModalVisible,
    openCsvModal,
    closeCsvModal,
    csvImportModalProps,
  };
}
